import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { MERCHANT_ADMIN_ROUTE, MESSAGES_ROUTE } from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateMessageForm } from "../components/CreateUpdateMessageForm"
import { FetchMessageResponse, MerchantMessagesQuery, fetchMessage, updateMessage } from "../messagesApi"
import { CreateEditMessageFormValues } from "../messagesTypes"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { DateTimeFactory, useDateTime } from "../../../../localization/useDateTime"

const selectMessage = (data: FetchMessageResponse, dateTimeFactory: DateTimeFactory) => {
    const message = data?.message
    if (!message) return null

    return {
        ...message,
        publishTime: message.publishTime ? dateTimeFactory.fromApi(message.publishTime) : null,
    }
}

const Message = forwardRef<FormikProps<CreateEditMessageFormValues>>((props, ref) => {
    const { messageId } = useParams()
    const navigate = useNavigate()
    const dateTimeFactory = useDateTime()

    const messageResult = useApiQuery(fetchMessage, {
        queryName: MerchantMessagesQuery.DETAILS,
        dependencies: {
            messageId: messageId ?? "",
        },
        enabled: Boolean(messageId),
        select: (data) => selectMessage(data, dateTimeFactory),
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateMessage } = useApiMutation(updateMessage, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantMessagesQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(MerchantMessagesQuery.DETAILS, { messageId: params.messageId }),
            })
            navigate(MESSAGES_ROUTE)
            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <CreateUpdateMessageForm
            ref={ref}
            mode="edit"
            initialValues={messageResult.data}
            onSubmit={(data) => {
                messageId && callUpdateMessage({ message: data, messageId: messageId })
            }}
        />
    )
})

export function UpdateMessage() {
    const formRef = useRef<FormikProps<CreateEditMessageFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }
    return (
        <>
            <LocalizedStrict id="edit-message-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MESSAGE_EDIT}
                    title="Edit message"
                    rightElement={
                        <LocalizedStrict id="edit-message-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-messages">
                            <BreadcrumbLink to={MESSAGES_ROUTE}>Push messages</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="nav-item-edit-message-header">
                            <Breadcrumb>Edit message</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Message ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
